import { BasicLogger, type ILogLevel } from '@featbit/js-client-sdk';
import { FbProvider } from '@featbit/openfeature-provider-js-client';
import { NOOP_PROVIDER, OpenFeature, type EvaluationContext } from '@openfeature/web-sdk';

/**
 * Port of olimpo's `afrodita/src/features/featbit.ts` for the CRA apps of poseidon.
 * Poseidon apps are built in Jenkins without per-environment variables, so the configuration is
 * passed to `initializeFeatbitProviders()` (usually from the app's `env.*.json`). `REACT_APP_*`
 * variables are still honoured as a fallback.
 */
export const TIRESIAS_DOMAINS = {
	front: 'front',
	base: 'base'
} as const;

type FeatbitDomain = (typeof TIRESIAS_DOMAINS)[keyof typeof TIRESIAS_DOMAINS];

export interface TiresiasConfig {
	sdkKeys?: Partial<Record<FeatbitDomain, string | null>>;
	streamingUri?: string | null;
	eventsUri?: string | null;
	logLevel?: ILogLevel | null;
}

let config: TiresiasConfig = {};

/**
 * User attributes sent to the Tiresias (FeatBit) evaluation API.
 *
 * - `keyId`/`name`: FeatBit built-in user identity.
 * - `managementCode`: brand of the app (defaults to `REACT_APP_BRAND_MANAGEMENT`).
 * - `path`: hierarchy path(s) of the user, so flags can target by territorial / zone / office / team.
 *
 * Every key other than `keyId`/`name` is forwarded to FeatBit as a customized property.
 */
export interface TiresiasUserContext {
	keyId?: string | null;
	name?: string | null;
	managementCode?: string | null;
	path?: string | null;
}

const ANONYMOUS = 'anonymous';

export function buildTiresiasContext(user: TiresiasUserContext = {}): EvaluationContext {
	const context: EvaluationContext = {
		keyId: user.keyId || ANONYMOUS,
		name: user.name || ANONYMOUS
	};

	const managementCode = user.managementCode || process.env.REACT_APP_BRAND_MANAGEMENT;
	if (managementCode) {
		context.managementCode = managementCode;
	}

	if (user.path) {
		context.path = user.path;
	}

	return context;
}

/**
 * Sets the evaluation context for every Tiresias domain so that the user's identity,
 * brand and hierarchy `path` reach the evaluation API. Safe to call at any time: domains that are
 * ready are re-identified immediately, domains still initialising pick the identity up when they finish.
 */
export function setTiresiasUserContext(user: TiresiasUserContext = {}) {
	if (typeof window === 'undefined') {
		return Promise.resolve();
	}

	const context = buildTiresiasContext(user);
	pendingContext = context;

	const targets = Object.values(TIRESIAS_DOMAINS).filter((domain) => readyDomains.has(domain));

	return Promise.allSettled(targets.map((domain) => OpenFeature.setContext(domain, context))).then(
		(results) => {
			results.forEach((result, index) => {
				if (result.status === 'rejected') {
					console.error(
						`[FeatBit] Failed to set context for "${targets[index]}" domain`,
						result.reason
					);
				}
			});
		}
	);
}

let initializationPromise: Promise<void> | null = null;

/**
 * Last identity requested through `setTiresiasUserContext`. Kept so that a domain that is still
 * initialising (or not yet initialised) picks it up as soon as it is ready. FeatBit's provider only
 * accepts context changes once its client exists, so we never push context to a non-ready domain.
 */
let pendingContext: EvaluationContext | null = null;
const readyDomains = new Set<FeatbitDomain>();

function getLogLevel(): ILogLevel {
	const rawLogLevel = config.logLevel || process.env.REACT_APP_FEATBIT_LOG_LEVEL;

	switch (rawLogLevel) {
		case 'debug':
		case 'info':
		case 'warn':
		case 'error':
		case 'none':
			return rawLogLevel;
		default:
			return process.env.REACT_APP_NODE_ENV === 'prod' || process.env.NODE_ENV === 'production'
				? 'none'
				: 'debug';
	}
}

function getSdkKey(domain: FeatbitDomain) {
	return (
		config.sdkKeys?.[domain] ||
		(domain === TIRESIAS_DOMAINS.front
			? process.env.REACT_APP_TIRESIAS_FRONT
			: process.env.REACT_APP_TIRESIAS_BASE)
	);
}

function redactSdkKey(sdkKey: string) {
	if (sdkKey.length <= 10) {
		return sdkKey;
	}

	return `${sdkKey.slice(0, 6)}...${sdkKey.slice(-4)}`;
}

/**
 * FeatBit user for the provider constructor. `FbProvider.initialize()` ignores the OpenFeature
 * context and identifies with this user, so we derive it from the pending identity when we have one.
 */
function buildInitialUser(context: EvaluationContext) {
	const { keyId, name, ...rest } = context;
	const customizedProperties = Object.entries(rest)
		.filter(([, value]) => value !== undefined && value !== null && value !== '')
		.map(([key, value]) => ({ name: key, value: String(value) }));

	return {
		keyId: String(keyId || ANONYMOUS),
		name: String(name || ANONYMOUS),
		customizedProperties: customizedProperties.length ? customizedProperties : undefined
	};
}

function buildProvider(domain: FeatbitDomain) {
	const sdkKey = getSdkKey(domain);

	if (!sdkKey) {
		console.warn(`[FeatBit] Missing SDK key for "${domain}" domain`);
		return null;
	}

	return {
		provider: new FbProvider({
			sdkKey,
			streamingUri:
				config.streamingUri ||
				process.env.REACT_APP_FEATBIT_STREAMING_URI ||
				'wss://tiresias-evaluation.gibobs.com',
			eventsUri:
				config.eventsUri ||
				process.env.REACT_APP_FEATBIT_EVENTS_URI ||
				'https://tiresias-evaluation.gibobs.com',
			user: buildInitialUser(pendingContext ?? buildTiresiasContext()),
			logger: new BasicLogger({
				level: getLogLevel(),
				destination: console.log
			})
		}),
		sdkKey
	};
}

async function initializeDomain(domain: FeatbitDomain) {
	console.log(`[FeatBit] Initializing "${domain}" domain...`);
	const domainProvider = buildProvider(domain);

	if (!domainProvider) {
		// No SDK key: bind the no-op provider so the domain is READY and hooks (including those
		// using `suspendUntilReady`) resolve to their defaults instead of waiting forever.
		await OpenFeature.setProviderAndWait(domain, NOOP_PROVIDER);
		console.warn(`[FeatBit] "${domain}" domain running without flags (defaults only)`);
		return;
	}

	console.info(
		`[FeatBit] Initializing "${domain}" domain with key ${redactSdkKey(domainProvider.sdkKey)}`
	);

	const contextAtBuild = pendingContext;

	await OpenFeature.setProviderAndWait(domain, domainProvider.provider);
	readyDomains.add(domain);

	console.info(`[FeatBit] "${domain}" domain is ready`);

	// The identity may have arrived while the client was connecting: re-identify with the latest one.
	if (pendingContext && pendingContext !== contextAtBuild) {
		await OpenFeature.setContext(domain, pendingContext);
	}
}

/**
 * Initialises every Tiresias domain once per browser session. `options` only applies on the first
 * call; later calls reuse the same promise.
 */
export function initializeFeatbitProviders(options: TiresiasConfig = {}) {
	if (typeof window === 'undefined') {
		return Promise.resolve();
	}

	if (initializationPromise) {
		return initializationPromise;
	}

	config = options;

	initializationPromise = Promise.allSettled([
		initializeDomain(TIRESIAS_DOMAINS.front),
		initializeDomain(TIRESIAS_DOMAINS.base)
	]).then((results) => {
		results.forEach((result, index) => {
			if (result.status === 'rejected') {
				const domain = index === 0 ? TIRESIAS_DOMAINS.front : TIRESIAS_DOMAINS.base;
				console.error(`[FeatBit] Failed to initialize "${domain}" domain`, result.reason);
			}
		});
	});

	return initializationPromise;
}
